import { useEffect, useRef, useState } from "react";
import { Form, Input, DatePicker, Skeleton } from "../../components/ui";
import {
  useAddChild,
  useGetChildById,
  useGetClassrooms,
  useSearchParents,
  useUpdateChild,
} from "../../query/childQuery";
import FormPhotoDrop from "../../components/FormPhotoDrop";
import swal from "../../utils/swal";
import dayjs from "dayjs";
import { useNavigate, useParams } from "react-router-dom";
import { UPLOADS_URL } from "../../config/constants";
import { stringListFormValue, serializeStringList } from "../../config/helpers/childHealth";
import HealthNotesFields from "./HealthNotesFields";
import "../teachers/addTeacher.css";
import "../fees/fees.css";
import "./children.css";

function asDateString(value) {
  if (!value) return "";
  if (typeof value === "string") return value;
  if (value.format) return value.format("YYYY-MM-DD");
  return "";
}

function parentLabel(parent) {
  if (!parent) return "";
  const father = `${parent.fatherFirstName || ""} ${parent.fatherLastName || ""}`.trim();
  const mother = `${parent.motherFirstName || ""} ${parent.motherLastName || ""}`.trim();
  return [father, mother].filter(Boolean).join(" / ") || parent.email || "Parent";
}

function AddChild() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);
  const [form] = Form.useForm();
  const { mutate: addChild, isPending: isAdding } = useAddChild();
  const { mutate: updateChild, isPending: isUpdating } = useUpdateChild();
  const { data: child, isLoading } = useGetChildById(isEdit ? id : undefined);
  const { data: classrooms = [] } = useGetClassrooms();
  const { data: parents = [] } = useSearchParents({});
  const intentRef = useRef("ACTIVE");
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState("");
  const isMutating = isAdding || isUpdating;

  useEffect(() => {
    if (!isEdit || !child) return;
    form.setFieldsValue({
      rollNumber: child.rollNumber || "",
      term: child.term || "",
      firstName: child.firstName || "",
      lastName: child.lastName || "",
      age: child.age ?? "",
      birthday: child.birthday ? dayjs(child.birthday) : "",
      classroom: child.classroom?._id || child.classroom || "",
      parent: child.parent?._id || child.parent || "",
      allergies: stringListFormValue(child.allergies),
      fears: stringListFormValue(child.fears),
      conditions: stringListFormValue(child.conditions),
      summary: stringListFormValue(child.summary),
      monthlyAmount: child.fee?.monthlyAmount ?? "",
      discountAmount: child.fee?.discountAmount ?? "",
      feeActive: child.fee?.active === false ? "false" : "true",
    });
    if (child.image) {
      setImagePreview(`${UPLOADS_URL}/${child.image}`);
    }
    intentRef.current = child.status === "INACTIVE" ? "INACTIVE" : "ACTIVE";
  }, [isEdit, child, form]);

  const buildFormData = (values) => {
    const formObject = new FormData();
    if (imageFile) formObject.append("image", imageFile);
    formObject.append("rollNumber", values.rollNumber || "");
    formObject.append("term", values.term || "");
    formObject.append("firstName", values.firstName || "");
    formObject.append("lastName", values.lastName || "");
    formObject.append("age", String(values.age ?? ""));
    formObject.append("birthday", asDateString(values.birthday));
    formObject.append("allergies", serializeStringList(values.allergies));
    formObject.append("fears", serializeStringList(values.fears));
    formObject.append("conditions", serializeStringList(values.conditions));
    formObject.append("summary", serializeStringList(values.summary));
    formObject.append("status", intentRef.current === "INACTIVE" ? "INACTIVE" : "ACTIVE");
    formObject.append("classroom", values.classroom || "");
    formObject.append("parent", values.parent || "");
    formObject.append("monthlyAmount", String(values.monthlyAmount ?? ""));
    formObject.append("discountAmount", String(values.discountAmount ?? ""));
    formObject.append("feeActive", values.feeActive === "false" ? "false" : "true");
    return formObject;
  };

  const onFinish = (values) => {
    if (!isEdit && !imageFile) {
      swal("Oops!", "Please upload a photo", "error");
      return;
    }

    const formObject = buildFormData(values);

    if (isEdit) {
      updateChild(
        { childId: id, data: formObject },
        {
          onSuccess: () => {
            swal("Success", "Student updated successfully", "success");
            navigate(`/children/${id}`);
          },
          onError: (error) => {
            swal("Error", error?.message || "Failed to update student", "error");
          },
        }
      );
      return;
    }

    addChild(
      { data: formObject },
      {
        onSuccess: (result) => {
          const payload = result?.data || result || {};
          const student = payload.child || payload;
          const drafted = intentRef.current === "INACTIVE";
          const lines = [drafted ? "Student saved as inactive" : "Student added successfully"];
          if (student?.rollNumber) lines.push(`Roll number: ${student.rollNumber}`);
          swal("Success", lines.join("\n"), "success");
          navigate("/children");
        },
        onError: (error) => {
          swal("Error", error?.message || "Failed to add student", "error");
        },
      }
    );
  };

  if (isEdit && isLoading) {
    return (
      <div className="tf-page">
        <Skeleton active />
      </div>
    );
  }

  return (
    <div className="tf-page">
      <Form
        form={form}
        layout="vertical"
        onFinish={onFinish}
        autoComplete="off"
        initialValues={{
          allergies: [""],
          fears: [""],
          conditions: [""],
          summary: [""],
          monthlyAmount: "",
          discountAmount: "",
          feeActive: "true",
        }}
      >
        <section className="tf-card">
          <h2 className="tf-card-head">Student Details</h2>
          <div className="tf-card-body">
            <FormPhotoDrop
              preview={imagePreview}
              required={!isEdit}
              alt="Student preview"
              onFile={(file) => {
                setImageFile(file);
                setImagePreview((prev) => {
                  if (prev && prev.startsWith("blob:")) URL.revokeObjectURL(prev);
                  return URL.createObjectURL(file);
                });
              }}
            />

            <div className="tf-field">
              <p className="tf-label">
                First Name <span>*</span>
              </p>
              <Form.Item
                name="firstName"
                className="tf-item mb-0"
                rules={[
                  { required: true, message: "Please enter first name" },
                  { min: 3, message: "First name must be at least 3 characters" },
                ]}
              >
                <Input placeholder="Aiden" />
              </Form.Item>
            </div>

            <div className="tf-field">
              <p className="tf-label">Last Name</p>
              <Form.Item name="lastName" className="tf-item mb-0">
                <Input placeholder="Brooks" />
              </Form.Item>
            </div>

            <div className="tf-field">
              <p className="tf-label">
                Roll Number <span>*</span>
              </p>
              <Form.Item
                name="rollNumber"
                className="tf-item mb-0"
                rules={[{ required: true, message: "Please enter roll number" }]}
              >
                <Input placeholder="BW2026-021" disabled={isEdit} />
              </Form.Item>
            </div>

            <div className="tf-field">
              <p className="tf-label">
                Term <span>*</span>
              </p>
              <Form.Item
                name="term"
                className="tf-item mb-0"
                rules={[{ required: true, message: "Please enter term" }]}
              >
                <Input placeholder="2026" />
              </Form.Item>
            </div>

            <div className="tf-field">
              <p className="tf-label">
                Birthday <span>*</span>
              </p>
              <Form.Item
                name="birthday"
                className="tf-item mb-0"
                rules={[{ required: true, message: "Please enter birthday" }]}
              >
                <DatePicker placeholder="Select birthday" />
              </Form.Item>
            </div>

            <div className="tf-field">
              <p className="tf-label">
                Age <span>*</span>
              </p>
              <Form.Item
                name="age"
                className="tf-item mb-0"
                rules={[{ required: true, message: "Please enter age" }]}
              >
                <Input type="number" min={1} max={20} placeholder="8" />
              </Form.Item>
            </div>
          </div>
        </section>

        <section className="tf-card">
          <h2 className="tf-card-head">Assignments</h2>
          <div className="tf-card-body">
            <div className="tf-field">
              <p className="tf-label">Class</p>
              <Form.Item name="classroom" className="tf-item mb-0">
                <select>
                  <option value="">No class assigned</option>
                  {classrooms.map((room) => (
                    <option key={room._id} value={room._id}>
                      {room.classroomName}
                      {room.classroomId ? ` (${room.classroomId})` : ""}
                    </option>
                  ))}
                </select>
              </Form.Item>
            </div>

            <div className="tf-field">
              <p className="tf-label">Parent</p>
              <Form.Item name="parent" className="tf-item mb-0">
                <select>
                  <option value="">No parent assigned</option>
                  {parents.map((parent) => (
                    <option key={parent._id} value={parent._id}>
                      {parentLabel(parent)}
                    </option>
                  ))}
                </select>
              </Form.Item>
            </div>
          </div>
        </section>

        <section className="tf-card">
          <h2 className="tf-card-head">Monthly fee</h2>
          <div className="tf-card-body">
            <p className="fee-send-lead">
              This amount is used when monthly vouchers are sent. Leave it at 0 to skip this student.
            </p>
            <div className="fee-send-grid">
              <div className="tf-field">
                <p className="tf-label">Monthly fee (PKR)</p>
                <Form.Item name="monthlyAmount" className="tf-item mb-0">
                  <Input type="number" min={0} placeholder="8000" />
                </Form.Item>
              </div>
              <div className="tf-field">
                <p className="tf-label">Discount (PKR)</p>
                <Form.Item name="discountAmount" className="tf-item mb-0">
                  <Input type="number" min={0} placeholder="0" />
                </Form.Item>
              </div>
              <div className="tf-field">
                <p className="tf-label">Fee status</p>
                <Form.Item name="feeActive" className="tf-item mb-0" initialValue="true">
                  <select>
                    <option value="true">Active</option>
                    <option value="false">Turned off</option>
                  </select>
                </Form.Item>
              </div>
            </div>
          </div>
        </section>

        <section className="tf-card">
          <h2 className="tf-card-head">Health & Notes</h2>
          <HealthNotesFields />
        </section>

        <div className="tf-actions">
          <button
            type="submit"
            className="tf-btn is-ghost"
            disabled={isMutating}
            onClick={() => {
              intentRef.current = "INACTIVE";
            }}
          >
            {isMutating && intentRef.current === "INACTIVE" ? "Saving..." : "Save as Draft"}
          </button>
          <button
            type="submit"
            className="tf-btn is-primary"
            disabled={isMutating}
            onClick={() => {
              intentRef.current = "ACTIVE";
            }}
          >
            {isMutating && intentRef.current === "ACTIVE"
              ? "Saving..."
              : isEdit
              ? "Save"
              : "Submit"}
          </button>
        </div>
      </Form>
    </div>
  );
}

export default AddChild;
